import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import { format } from 'date-fns-tz'
import { useForm } from 'react-hook-form'
import DatePicker from 'react-datepicker'

import Card from '@/shared/Card'
import PaymentFields, { withoutHiddenPay } from '@/shared/PaymentFields'
import PrimaryButton from '@/shared/Buttons/Primary'
import { randomToastSuccess } from '@/shared/Toast'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'

const deadlineTimes = ['00:00', '00:30', '01:00', '01:30', '02:00', '02:30', '03:00', '03:30', '04:00', '04:30',
  '05:00', '05:30', '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
  '11:00', '11:30', '12:00', '12:30']

const selectClass = 'block w-full rounded-md border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-cccpurple focus:outline-none focus:ring-cccpurple dark:bg-gray-900 dark:text-gray-100 sm:text-sm'

export const Required = () => <span className='ml-1 text-xs font-normal text-cccorange'>required</span>

// `ref.save()` resolves true when the form is clean or saved, false when
// validation or the server said no. The wizard's "Save and continue" leans on
// it so nobody has to click Save and then Next.
const RequiredInfoForm = forwardRef(({ initialProject, onSaved, embedded }, ref) => {
  const [, setToast] = useGlobalState('toast')
  const tipTapIdx = useRef(1)
  const [project, setProject] = useState(initialProject)
  // new Date(null) is the Unix epoch, so a deadline-less project needs its own fallback here too.
  const [deadlineDate, setDeadlineDate] = useState(initialProject.deadlineDate ? new Date(initialProject.deadlineDate) : new Date())
  const [timezones, setTimezones] = useState([])
  const [payment, setPayment] = useState(initialProject.payment)
  const { putpostRequest, getRequest } = useQuery()
  const form = useForm({
    defaultValues: {
      name: initialProject.name === 'Untitled project' ? '' : initialProject.name,
      payment: initialProject.payment,
      payment_details: initialProject.paymentDetails,
      payment_range: initialProject.paymentRange,
      payment_interval: initialProject.paymentInterval,
      payment_currency: initialProject.paymentCurrency,
      payment_deferred_type: initialProject.paymentDeferredType,
      payment_amount_min: initialProject.paymentAmountMin,
      payment_amount_max: initialProject.paymentAmountMax,
      expected_commitment_amount: initialProject.expectedCommitmentAmount,
      expected_commitment_interval: initialProject.expectedCommitmentInterval,
      time_zone: initialProject.timeZone,
      deadline_date: initialProject.deadlineDate || format(new Date(), 'yyyy-MM-dd'),
      deadline_time: deadlineTimes.includes(initialProject.deadlineTime) ? initialProject.deadlineTime : deadlineTimes[0],
      deadline_ampm: initialProject.deadlineAmpm
    }
  })
  const { formState, reset, register, unregister, handleSubmit, setValue, getValues, setError, setFocus, trigger } = form
  const { isDirty, errors } = formState
  const [loading, setLoading] = useState(false)
  const pendingSave = useRef(null)

  useEffect(() => {
    getRequest('/api/v3/static/timezones', {}, (err, jsonData) => {
      if (err) { /* handled in hook */ return }
      if (jsonData.timezones) {
        setTimezones(jsonData.timezones)
        setValue('time_zone', initialProject.timeZone, { shouldDirty: false, shouldValidate: false })
      }
    })
  }, [])

  useEffect(() => { if (timezones.length > 0) { setFocus('name') } }, [timezones.length])

  useEffect(() => {
    register('deadline_date', { required: true })
    register('payment', { required: true })
    register('payment_details')
    return () => { unregister('deadline_date'); unregister('payment'); unregister('payment_details') }
  }, [register])

  const handleDateChange = (date) => {
    setDeadlineDate(date)
    setValue('deadline_date', format(date, 'yyyy-MM-dd'), { shouldDirty: true, shouldValidate: true })
  }

  const handlePaymentChange = (value) => {
    setPayment(value)
    setValue('payment', value, { shouldDirty: true, shouldValidate: true })
  }

  const keyPress = (e) => {
    if (e.target.name === 'name' && e.keyCode === 13) { e.preventDefault() }
    if ((e.metaKey || e.ctrlKey) && e.keyCode === 13) { handleSubmit(onSubmit)() }
  }

  const finish = (ok) => { if (pendingSave.current) { pendingSave.current(ok); pendingSave.current = null } }

  const onSubmit = (data) => {
    setLoading(true)
    putpostRequest(`/api/v3/manage/projects/${project.id}`, 'PATCH', { project: withoutHiddenPay(data) }, (err, jsonData) => {
      setLoading(false)
      if (err) { // 422 code
        if (typeof err !== 'string') { Object.entries(err).forEach(([key, value]) => { setError(key, value) }) }
        finish(false)
        return
      }
      reset({ ...getValues() })
      setProject({ ...jsonData.project })
      if (onSaved) { onSaved(jsonData.project) }
      if (!embedded) { setToast(randomToastSuccess()) }
      finish(true)
    })
  }

  useImperativeHandle(ref, () => ({
    save: () => new Promise((resolve) => {
      // Clean form: still validate, so a blank name can't slip past.
      if (!isDirty) { trigger().then(resolve); return }
      pendingSave.current = resolve
      handleSubmit(onSubmit, () => finish(false))()
    })
  }), [isDirty, handleSubmit])

  // Wait for the timezone list before rendering. The <select> for time_zone
  // matches the form's default value against an <option>; if options aren't
  // loaded yet the browser silently falls back to the first option in DOM
  // order, which the setValue retry doesn't always undo.
  if (timezones.length === 0) {
    return <div className='p-8 text-center text-sm text-gray-500 dark:text-gray-400'>Fetching every timezone on Earth. One sec.</div>
  }

  const footer = embedded ? null : <div className='flex justify-end'>
    <PrimaryButton disabled={!isDirty} onClick={handleSubmit(onSubmit)} loading={loading} isDirty={isDirty} text='Save' />
  </div>

  return <div className='md:grid md:grid-cols-4 md:gap-6'>
    <div className='mb-4 md:mb-0'>
      <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>The basics</h3>
      <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>A name people can find, a deadline they can plan around, and whether there's money in it.</p>
      <p className='mt-3 text-sm text-gray-600 dark:text-gray-300'>Short deadlines get more auditions. You can always extend later, and submissions keep coming in until the deadline even after you cast someone.</p>
    </div>
    <Card title='Basics' footer={footer}>
      <form className='space-y-6'>
        <div>
          <label htmlFor='project_name' className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Project name <Required /></label>
          <input id='project_name' onKeyDown={keyPress} type='text' className={errors.name ? 'errors mt-1' : 'mt-1'} {...register('name', { required: true })} placeholder='Goku vs Mario: The Love Story' />
          { errors.name && <div className='mt-1 text-sm text-red-600'>Every project needs a name. Even a bad one.</div> }
          <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>Searchable unless you mark the project unlisted later.</p>
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Submissions close <Required /></label>
          <div className='mt-1 grid grid-cols-2 gap-3 sm:grid-cols-4'>
            <div className='col-span-2 sm:col-span-1'>
              <DatePicker selected={deadlineDate} onChange={handleDateChange} minDate={new Date()} nextMonthButtonLabel='>' previousMonthButtonLabel='<' />
            </div>
            <select {...register('deadline_time', { required: true })} className={selectClass} aria-label='Time'>
              { deadlineTimes.map((time) => <option key={time} value={time}>{time}</option>) }
            </select>
            <select {...register('deadline_ampm', { required: true })} className={selectClass} aria-label='AM or PM'>
              <option>AM</option><option>PM</option>
            </select>
            <select {...register('time_zone', { required: true })} className={`${selectClass} col-span-2 sm:col-span-4`} aria-label='Timezone'>
              { timezones.map((tz) => <option key={tz.computer} value={tz.computer}>{tz.human.replace(/_/g, ' ')}</option>) }
            </select>
          </div>
          <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>The timezone decides the exact minute the door shuts.</p>
        </div>

        <div className='border-t border-gray-200 pt-6 dark:border-gray-700'>
          <PaymentFields form={form} payment={payment} onPaymentChange={handlePaymentChange} tipTapIdx={tipTapIdx.current} subject='project' />
          <p className='mt-2 text-xs text-gray-500 dark:text-gray-400'>Paying different rates per role? Set the default here and override on each role.</p>
        </div>
      </form>
      { errors.base && <div className='mt-2 text-sm text-red-600'>{errors.base[0]}</div> }
    </Card>
  </div>
})

RequiredInfoForm.displayName = 'RequiredInfoForm'

export default RequiredInfoForm

RequiredInfoForm.propTypes = {
  initialProject: PropTypes.object.isRequired,
  onSaved: PropTypes.func,
  embedded: PropTypes.bool
}
